/**
 * @fileoverview Gantt Height Manager - Event-driven height management for gantt containers
 * @module GanttHeightManager
 */

import { CONFIG } from './heightConstants.js';
import {
  cacheGanttContainers,
  clearCache,
  recacheContainers
} from './domCache.js';
import {
  checkDrawerState,
  checkBABState,
  hasRelevantChanges
} from './stateDetectors.js';
import {
  calculateTargetHeight,
  updateGanttHeight,
  clearUpdateTimeout
} from './heightUpdater.js';

// Manager state
let isActive = false;
let mutationObserver = null;
let resizeObserver = null;

// Application state
const state = {
  isActivityCardOpen: false,
  isBABOpen: false,
  lastHeight: null
};

/**
 * Handle state changes and update height accordingly
 * @param {boolean} newDrawerState - Whether drawer is open
 * @param {boolean} newBABState - Whether BAB is visible
 */
async function handleStateChange(newDrawerState, newBABState) {
  let hasChanges = false;

  if (newDrawerState !== state.isActivityCardOpen) {
    state.isActivityCardOpen = newDrawerState;
    hasChanges = true;
  }

  if (newBABState !== state.isBABOpen) {
    state.isBABOpen = newBABState;
    hasChanges = true;
  }

  if (hasChanges) {
    const targetHeight = calculateTargetHeight(
      state.isBABOpen,
      state.isActivityCardOpen
    );
    state.lastHeight = await updateGanttHeight(targetHeight, state.lastHeight);
  }
}

/**
 * Start the height manager
 * @public
 */
function start() {
  if (isActive) return;

  isActive = true;

  // Cache initial containers
  cacheGanttContainers();

  // Initial state check
  const initialDrawerState = checkDrawerState();
  const initialBABState = checkBABState();
  handleStateChange(initialDrawerState, initialBABState);

  // MutationObserver for efficient DOM change detection
  mutationObserver = new MutationObserver((mutations) => {
    const { hasDrawerChanges, hasBABChanges } = hasRelevantChanges(mutations);

    // Only check what changed
    if (hasDrawerChanges || hasBABChanges) {
      const currentDrawerState = hasDrawerChanges
        ? checkDrawerState(mutations)
        : state.isActivityCardOpen;
      const currentBABState = hasBABChanges
        ? checkBABState(mutations)
        : state.isBABOpen;

      handleStateChange(currentDrawerState, currentBABState);
    }
  });

  // Observe with optimized configuration
  mutationObserver.observe(document.body, CONFIG.MUTATION_OBSERVER_OPTIONS);

  // ResizeObserver for container changes (if supported)
  if (window.ResizeObserver) {
    const ganttContainer = cacheGanttContainers();
    if (ganttContainer) {
      resizeObserver = new ResizeObserver(() => {
        recacheContainers();
      });
      resizeObserver.observe(ganttContainer);
    }
  }
}

/**
 * Stop the height manager and cleanup
 * @public
 */
function stop() {
  isActive = false;

  // Clear all timers
  clearUpdateTimeout();

  // Disconnect observers
  if (mutationObserver) {
    mutationObserver.disconnect();
    mutationObserver = null;
  }

  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }

  // Clear cached references to prevent memory leaks
  clearCache();
}

// Exports
export { start, stop };

export default {
  start,
  stop
};
